import { allCategories } from "../deals/CategoryFilter";
import { ALL_CATEGORIES } from "../deals/dealList/constants";
import {
  ALL_STATES,
  STATE_TO_AREAS,
} from "../deals/dealList/constants/malaysiaLocations";
import { parseDealCategoryParam } from "../../utils/dealCategoryParam";

/**
 * Reminder chips = deal quick-filter bar categories (minus birthday) plus any extra
 * cuisines from the deal filter sheet (e.g. Indian, Convenience) so browse prefs map 1:1.
 */
const baseReminderCategories = allCategories.filter(
  (c) => c.value !== "birthday",
);
const seenValues = new Set(baseReminderCategories.map((c) => c.value));
const extraFromDealFilterSheet = ALL_CATEGORIES.filter(
  (c) => !seenValues.has(c.value),
);

export const REMINDER_CATEGORIES = [
  ...baseReminderCategories,
  ...extraFromDealFilterSheet,
];

const FLAT_AREA_SET = new Set();
for (const list of Object.values(STATE_TO_AREAS)) {
  list.forEach((a) => FLAT_AREA_SET.add(a));
}

/** Same limit as TelegramReminderSetup picker */
export const REMINDER_AREA_SELECTION_CAP = 3;

/**
 * Converts saved deal browse `filterPreferences.area` (single string) into
 * the multi-select array Telegram reminders expect.
 *
 * Supports: township strings in STATE_TO_AREAS, state names → first townships up to cap,
 * ignores `"current"` until resolved server-side elsewhere.
 *
 * @param {unknown} areaFromPrefs
 * @returns {string[]}
 */
export const reminderAreasFromDealAreaPreference = (areaFromPrefs) => {
  const s = typeof areaFromPrefs === "string" ? areaFromPrefs.trim() : "";
  if (!s || s === "current") return [];

  if (FLAT_AREA_SET.has(s)) {
    return [s];
  }

  if (
    ALL_STATES.includes(s) &&
    Array.isArray(STATE_TO_AREAS[s]) &&
    STATE_TO_AREAS[s].length
  ) {
    return STATE_TO_AREAS[s].slice(0, REMINDER_AREA_SELECTION_CAP);
  }

  const lower = s.toLowerCase();
  const fuzzy = [...FLAT_AREA_SET].find((a) => a.toLowerCase() === lower);
  return fuzzy ? [fuzzy] : [];
};

/**
 * Maps `filterPreferences.category` to reminder `filters.category` values.
 * Deal browse stores comma-separated tokens (see `serializeDealCategories`); older
 * single-value prefs are still supported.
 *
 * @param {unknown} categoryFromPrefs
 * @returns {string[]}
 */
export const reminderCategoriesFromDealCategoryPreference = (
  categoryFromPrefs,
) => {
  const tokens = parseDealCategoryParam(categoryFromPrefs);
  if (tokens.length === 0) return [];

  const out = [];
  const used = new Set();
  for (const token of tokens) {
    const trimmed =
      typeof token === "string" ? token.trim().replace(/\s+/g, " ") : "";
    if (!trimmed) continue;

    const exact = REMINDER_CATEGORIES.find((c) => c.value === trimmed);
    const resolved =
      exact ||
      REMINDER_CATEGORIES.find(
        (c) => c.value.toLowerCase() === trimmed.toLowerCase(),
      );
    if (resolved && !used.has(resolved.value)) {
      used.add(resolved.value);
      out.push(resolved.value);
    }
  }
  return out;
};

/** Canonical ordered list of reminder category filter values (chips). */
export const allReminderCategoryValues = () =>
  REMINDER_CATEGORIES.map((c) => c.value).filter(Boolean);

/** Empty selection or a full set means “all categories” (same semantics as deal filter sheet). */
export const isAllReminderCategoriesSelected = (selected) => {
  const arr = Array.isArray(selected) ? selected : [];
  if (arr.length === 0) return true;
  const all = allReminderCategoryValues();
  if (arr.length !== all.length) return false;
  const s = new Set(arr);
  return all.every((v) => s.has(v));
};

/** User has chosen categories (explicit chips or default All). */
export const hasReminderCategorySelection = (selected) => {
  const arr = Array.isArray(selected) ? selected : [];
  return arr.length > 0 || isAllReminderCategoriesSelected(arr);
};

/** Normalize saved/prefilled categories to [] when the full set is stored explicitly. */
export const normalizeReminderCategoriesForForm = (selected) => {
  const arr = Array.isArray(selected) ? selected : [];
  return isAllReminderCategoriesSelected(arr) ? [] : arr;
};
